'use client';
import { useCallback, useMemo } from 'react';
import { FsHeaderV2, FsPopoverMenuItem } from '@printi/ds-offset-react-core';
import { usePathname, useRouter } from 'next/navigation';
import { signOut } from 'next-auth/react';
import { useLayout } from '@mf/common/components/Media/Layout';
import { ReferralBanner } from '@mf/common/components/index';
import { useCartStore } from '@mf/common/store/cart/cart.store';
import { MegaMenuStrapiResponse } from '@mf/navigation/repositories/aecomStrapiCms/megaMenu/types';
import { userMenuItems, userMenuItemsMobile } from '../constants';
import { processMenuData } from '../utils';
import * as S from './Header.styles';
import { ValidUserSession } from './ValidUserSession';

export interface ClientHeaderV2Props {
  megaMenuItems: MegaMenuStrapiResponse['data'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  menuItems: any[];
  isAuthenticated: boolean;
  customerData: {
    first_name?: string;
  };
  showSearchButton?: boolean;
}

export const ClientHeaderV2 = ({
  megaMenuItems,
  menuItems,
  isAuthenticated,
  customerData,
}: ClientHeaderV2Props) => {
  const { push } = useRouter();
  const pathname = usePathname();
  const { isMobile, setShowSearchPage } = useLayout();

  const menuItemsByPath = useMemo(() => {
    return processMenuData(menuItems, pathname);
  }, [menuItems, pathname]);

  const {
    clearCart,
    lastCartItemsTotal,
    setLastCartItems,
    setLastCartId,
    setOfflineCartId,
    setLastCartItemsTotal,
    setCustomerId,
  } = useCartStore();

  const clearCartData = useCallback(() => {
    setLastCartItems([]);
    setLastCartId(null);
    setOfflineCartId(null);
    setCustomerId(null);
    setLastCartItemsTotal(0);
  }, [
    setLastCartItems,
    setLastCartId,
    setOfflineCartId,
    setLastCartItemsTotal,
    setCustomerId,
  ]);

  const isCheckout = (pathname || '').includes('finalizar-compra');

  const onLoginHandler = () => {
    push(`/login?callbackUrl=${pathname}`);
  };

  const onCartClickHandler = () => {
    push('/carrinho');
  };

  const onClickMenu = (link: string) => {
    if (link) {
      onMenuClickHandler('menu');
      push(link);
    }
  };

  const onClickProduct = (slug: string) => {
    if (slug) {
      onMenuClickHandler('product');
      push(`/${slug}`);
    }
  };

  const onClickCategory = (link: string) => {
    if (link) {
      onMenuClickHandler('category');
      push(link);
    }
  };

  const onClickSeeAll = (link: string) => {
    if (link) {
      onMenuClickHandler('see_all');
      push(link);
    }
  };

  const onMenuClickHandler = (type: string) => {
    window.dataLayer.push({
      event: 'genericEvent',
      event_name: `change_route_${type}`,
    });
  };

  const signOutButton = useMemo(() => {
    const handleSelectSignout = () => {
      signOut({
        redirect: false,
      });
      clearCart();
      setTimeout(() => {
        push('/');
        clearCartData();
      }, 1500);
    };

    // eslint-disable-next-line react/display-name
    return () => (
      <FsPopoverMenuItem
        style={{ width: 'auto' }}
        label="Sair"
        icon="LogoutLine"
        onSelected={() => handleSelectSignout()}
      />
    );
  }, [clearCart, clearCartData, push]);

  const renderDesktopMenu = useMemo(() => {
    return (
      <>
        {userMenuItems.map((props, index) => (
          <FsPopoverMenuItem {...props} key={`item-${index}`} />
        ))}
        {signOutButton()}
      </>
    );
  }, [signOutButton]);

  const renderMobileMenu = useMemo(() => {
    return (
      <>
        {userMenuItemsMobile.map((props, index) => (
          <FsPopoverMenuItem {...props} key={`item-${index}`} />
        ))}
        <ReferralBanner />
        <S.MobileMenuFooter>
          <S.ProfileButton
            label={customerData?.first_name}
            showButton={false}
          />
          {signOutButton()}
        </S.MobileMenuFooter>
      </>
    );
  }, [customerData, signOutButton]);

  return (
    <S.ContainerV2>
      <FsHeaderV2
        menuBar={{
          allProducts: megaMenuItems?.allCategories || [],
          menuOptions: megaMenuItems?.menuOptions || [],
          menuItems: menuItemsByPath,
          onClickProduct,
          onClickCategory,
          onClickSeeAll,
          onClickMenu,
        }}
        searchPlaceholder="Busque por produtos aqui..."
        onLogin={onLoginHandler}
        onCartClick={onCartClickHandler}
        headerSearchPlaceholder="Qual produto você está procurando?"
        logged={isAuthenticated}
        userName={customerData?.first_name}
        amountCartItems={lastCartItemsTotal}
        showAction={isCheckout ? false : true}
        ghost={isCheckout}
        onSearchClick={() => setShowSearchPage(true)}
        userDropdownElements={
          <>{isMobile ? renderMobileMenu : renderDesktopMenu}</>
        }
      />

      <ValidUserSession />
    </S.ContainerV2>
  );
};
